<template>
  <div class="card">
    <div class="card-body table-responsive position-relative">
      <b-table
        striped
        bordered
        foot-clone
        show-empty
        responsive="sm"
        :fields="fields"
        :busy="isLoading"
        class="datatables"
        :items="tableItems"
        :tbody-tr-class="rowClass"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle" />
          </div>
        </template>

        <template #cell(NM_PRODUTOR_RURAL)="data">
          {{ formatNome(data.value) }}
        </template>

        <template v-if="!importacao" v-slot:cell(ST_PROJETO)="data">
          <span
            :class="stImportacao(data.value)"
            class="badge badge-pill font-size-12"
          >
            {{ data.value }}
          </span>
        </template>

        <template v-else v-slot:cell(ST_IMPORTACAO)="data">
          <span
            :class="stImportacaoAcoes(data.value)"
            class="badge badge-pill font-size-12"
          >
            {{ data.value }}
          </span>
        </template>

        <template #cell(interacao)="data">
          <div
            v-if="
              (data.item.ST_PROJETO !== 'ELABORADO' && !importacao) ||
                (importacao && data.item.ST_IMPORTACAO === 'EXPORTADO')
            "
          >
            <b-button
              size="sm"
              variant="primary"
              class="me-1"
              :disabled="loadingAcoes || isLoading"
              data-tooltip="Visualizar"
              data-tooltip-location="left"
              @click="emitirEditarItem(data.item)"
            >
              <i class="mdi mdi-eye"></i>
            </b-button>
          </div>
          <div class="d-flex gap-2" v-else>
            <b-button
              size="sm"
              variant="primary"
              data-tooltip="Editar"
              :disabled="loadingAcoes || isLoading"
              data-tooltip-location="left"
              @click="emitirEditarItem(data.item)"
            >
              <i class="mdi mdi-pencil"></i>
            </b-button>
            <b-button
              size="sm"
              variant="danger"
              data-tooltip="Excluir"
              data-tooltip-location="left"
              @click="deletarProjetoDoLote(data.item)"
              v-if="data.item.ST_IMPORTACAO === 'INCOMPLETO'"
              class="d-flex align-items-center justify-content-center"
              :disabled="
                loadingItem === data.item.SQ_ASTEC_INTEGRACAO_ITEM || isLoading
              "
            >
              <i
                v-if="loadingItem !== data.item.SQ_ASTEC_INTEGRACAO_ITEM"
                class="mdi mdi-trash-can"
              >
              </i>
              <b-spinner
                small
                variant="white"
                v-if="loadingItem === data.item.SQ_ASTEC_INTEGRACAO_ITEM"
              >
              </b-spinner>
            </b-button>
          </div>
        </template>

        <!-- Templates do footer -->
        <template #foot()="">
          <h6></h6>
        </template>
        <template #foot(NM_PRODUTOR_RURAL)="">
          <div class="text-end">
            <span class="text-dark fw-bold">TOTAIS:</span>
          </div>
        </template>
        <template #foot(VL_ORCADO)="">
          <div class="text-end">
            <span class="text-dark fw-bold">{{
              $tools.converteReal(totalVlOrcado)
            }}</span>
          </div>
        </template>
        <template #foot(VL_ASTEC)="">
          <div class="text-end">
            <span class="text-dark fw-bold">{{
              $tools.converteReal(totalVlAstec)
            }}</span>
          </div>
        </template>
        <template #foot(VL_LIBERADO)="">
          <div class="text-end">
            <span class="text-dark fw-bold">{{
              $tools.converteReal(totalVlLiberado)
            }}</span>
          </div>
        </template>
        <template #foot(VL_SALDO_FOLHA)="">
          <div class="text-end">
            <span class="text-dark fw-bold">{{
              $tools.converteReal(totalVlSaldoFolha)
            }}</span>
          </div>
        </template>
        <template #foot(VL_SALDO_RDV)="">
          <div class="text-end">
            <span class="text-dark fw-bold">{{
              $tools.converteReal(totalVlSaldoRdv)
            }}</span>
          </div>
        </template>

        <template #empty>
          <div class="text-center">
            <span class="text-muted">{{ defaultMensagem }}</span>
          </div>
        </template>
      </b-table>

      <div class="d-flex justify-content-end mt-3">
        <b-pagination
          v-model="pageProxy"
          :total-rows="total"
          :per-page="pageSize"
          :disabled="isLoading"
          class="pagination pagination-rounded mb-0"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import clienteRuralMixin from "@/tools/mixins/clienteRuralMixin.js";
import { LoteImportacaoService } from "@/services/astec/loteImportacao";
import { paginacaoProjetoPorLoteHelper } from "@/store/helpers/vuex/astec.js";

export default {
  mixins: [clienteRuralMixin],
  name: "GridProjetosPorLote",

  props: {
    filtro: {
      type: Object,
      default: () => ({})
    },
    items: {
      type: Object,
      default: () => ({ data: [], total: 0, current_page: null, per_page: 10 })
    },
    importacao: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    defaultMensagem: {
      type: String,
      default: "Nenhum registro disponível."
    }
  },

  data() {
    return {
      fields: [
        { key: "NR_PROJETO", label: "N° Projeto", sortable: true },
        {
          key: "NM_INSTITUICAO_FINANCEIRA",
          label: "Instituição Financeira",
          sortable: true
        },
        { key: "NM_LINHA_CREDITO", label: "Linha Crédito", sortable: true },
        {
          key: "DS_SAFRA",
          label: "Safra",
          sortable: true,

          formatter: (value, key, item) => {
            return item.CD_SAFRA ? item.DS_SAFRA : item.DS_SAFRA;
          }
        },
        { key: "DS_TP_PROJETO", label: "Tipo Projeto", sortable: true },
        {
          key: "DS_ITEM_AGROPECUARIO",
          label: "Item Agropecuário",
          sortable: true
        },
        { key: "NM_PRODUTOR_RURAL", label: "Cliente Rural", sortable: true },
        {
          key: "VL_ORCADO",
          label: "Valor Orçado",
          sortable: true,
          formatter: value => this.$tools.converteReal(value)
        },
        {
          key: "VL_ASTEC",
          label: "Valor Astec",
          sortable: true,
          formatter: value => this.$tools.converteReal(value)
        },
        {
          key: "VL_LIBERADO",
          label: "Valor Liberado",
          sortable: true,
          formatter: value => this.$tools.converteReal(value)
        },
        {
          key: "VL_SALDO_FOLHA",
          label: "Saldo FOL",
          sortable: true,
          formatter: value => this.$tools.converteReal(value)
        },
        {
          key: "VL_SALDO_RDV",
          label: "Saldo RDV",
          sortable: true,
          formatter: value => this.$tools.converteReal(value)
        },
        { key: "ST_PROJETO", label: "Status", sortable: true },
        { key: "interacao", label: "Ações" }
      ],
      localItems: [],
      loadingItem: null,
      loadingAcoes: false
    };
  },

  computed: {
    ...paginacaoProjetoPorLoteHelper({
      state: ["currentPage", "pageSize", "total", "loading"],
      getters: [
        "currentPage",
        "pageSize",
        "total",
        "loading",
        "apiParams",
        "totalPages",
        "hasNextPage",
        "hasPrevPage",
        "rangeStart",
        "rangeEnd"
      ],
      actions: []
    }).computed,

    pageProxy: {
      get() {
        return this.currentPage;
      },
      set(val) {
        this.setPage(val);
        this.onPageChange(val);
      }
    },

    totalVlOrcado() {
      return this.calcularTotal("VL_ORCADO");
    },
    totalVlAstec() {
      return this.calcularTotal("VL_ASTEC");
    },
    totalVlLiberado() {
      return this.calcularTotal("VL_LIBERADO");
    },
    totalVlSaldoFolha() {
      return this.calcularTotal("VL_SALDO_FOLHA");
    },
    totalVlSaldoRdv() {
      return this.calcularTotal("VL_SALDO_RDV");
    },
    noItems() {
      if (this.isLoading) return false;
      if (!this.items) return true;
      if (Array.isArray(this.items) && this.items.length === 0) return true;
      if (this.items.data && this.items.data.length === 0) return true;
      return false;
    },
    tableItems() {
      if (this.items && Array.isArray(this.items.data)) {
        return this.items.data;
      }
      return [];
    }
  },

  created() {
    if (this.importacao && !this.fields.find(f => f.key === "ST_IMPORTACAO")) {
      this.fields.splice(12, 1, {
        key: "ST_IMPORTACAO",
        label: "Status Importação",
        sortable: true
      });
    }
  },

  methods: {
    ...paginacaoProjetoPorLoteHelper({
      actions: [
        "setPage",
        "setPageSize",
        "setTotal",
        "setLoading",
        "syncWithApi"
      ]
    }).methods,

    rowClass(item, type) {
      if (!item || type !== "row") return "";
      if (item.ST_PROJETO === "EXCLUIDO") {
        return "table-danger";
      }
      return "";
    },

    calcularTotal(campo) {
      if (!this.items.data || this.items.data.length === 0) return 0;
      return this.items.data.reduce((total, item) => {
        return total + parseFloat(item[campo] || 0);
      }, 0);
    },

    emitirEditarItem(data) {
      this.$store.commit("PARAMETROS_PROJETO_ASTEC", data);
      localStorage.setItem(
        "exibirCamposImportacao",
        this.importacao.toString()
      );
      this.$router.push("/astec/projetos/novoProjeto");
    },

    deletarProjetoDoLote(projetoDoLote) {
      let sqItem = projetoDoLote.SQ_ASTEC_INTEGRACAO_ITEM;
      this.confirmarExclusao({
        item: sqItem,
        servicoExclusao: LoteImportacaoService.excluirItemDoLote,
        mensagemConfirmacao:
          "Você realmente deseja excluir este projeto do lote? Essa ação não pode ser mais desfeita."
      });
    },

    onPageChange(page) {
      this.$emit("projetoPorLoteFiltrados", {
        ...this.filtro,
        page,
        per_page: this.pageSize
      });
    },

    formatNome(nome) {
      if (!nome) return "";
      return nome
        .toLowerCase()
        .split(" ")
        .map(palavra => palavra.charAt(0).toUpperCase() + palavra.slice(1))
        .join(" ");
    },

    stImportacao(valorImportacao) {
      const classes = {
        ELABORADO: "badge-soft-info",
        LIQUIDADO: "badge-soft-dark",
        LIBERADO: "badge-soft-success",
        EXCLUIDO: "badge-soft-danger",
        INCOMPLETO: "badge-soft-primary",
        "BAIXA PARCIAL": "badge-soft-warning"
      };
      return classes[valorImportacao] || "badge-secondary";
    },

    stImportacaoAcoes(valorImportacaoAcao) {
      const classes = {
        COMPLETO: "badge-soft-info",
        EXPORTADO: "badge-soft-success",
        INCOMPLETO: "badge-soft-danger"
      };
      return classes[valorImportacaoAcao] || "badge-secondary";
    },

    confirmarExclusao({ item, mensagemConfirmacao, servicoExclusao }) {
      Swal.fire({
        title: "Tem certeza?",
        text: mensagemConfirmacao,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sim, excluir!",
        cancelButtonText: "Cancelar"
      }).then(result => {
        if (result.isConfirmed) {
          this.loadingItem = item;

          servicoExclusao(item)
            .then(response => {
              const mensagemSucesso =
                response?.message || "Exclusão realizada com sucesso!";

              if (this.items && this.items.data) {
                const index = this.items.data.findIndex(
                  idx => idx.SQ_ASTEC_INTEGRACAO_ITEM === item
                );

                if (index !== -1) {
                  this.items.data.splice(index, 1);
                  this.items = { ...this.items };

                  // Atualiza o total no store
                  if (this.items.total) {
                    const novoTotal = this.items.total - 1;
                    this.items.total = novoTotal;
                    this.setTotal(novoTotal);
                  }

                  // Se a página ficar vazia e não for a primeira página, volta uma página
                  if (this.items.data.length === 0 && this.currentPage > 1) {
                    const novaPagina = this.currentPage - 1;
                    this.setPage(novaPagina);
                    this.$emit("projetoPorLoteFiltrados", {
                      ...this.filtro,
                      page: novaPagina,
                      per_page: this.pageSize
                    });
                  } else {
                    // Só emite o evento se não precisou mudar de página
                    this.$emit("projetoPorLoteFiltrados", {
                      ...this.filtro,
                      page: this.currentPage,
                      per_page: this.pageSize
                    });
                  }
                }
              }

              Swal.fire({
                icon: "success",
                title: "Sucesso",
                text: mensagemSucesso,
                timer: 1500,
                showConfirmButton: false
              });
            })
            .catch(error => {
              let mensagemErro = "Erro ao realizar exclusão";
              if (error?.response?.data?.message) {
                mensagemErro = error.response.data.message;
              } else if (error?.message) {
                mensagemErro = error.message;
              }
              this.$tools.alerta(mensagemErro, "error");
            })
            .finally(() => {
              this.loadingItem = null;
            });
        }
      });
    }
  }
};
</script>

<style scoped>
.sortable-header {
  cursor: pointer;
  display: flex;
  align-items: center;
}

.sort-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: 5px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
